import { type ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { type ClientInviteApplyStatus } from '@apm/contracts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/states';
import { useAuth } from '../features/auth/auth-context';
import { GoogleSignIn } from '../features/auth/components/google-sign-in';
import { issueApi } from '../features/issues/api';
import { errorMessage } from '../lib/error-message';

/**
 * Trang của **link mời khách hàng** — APM-CLINV-c, docs/plan-issue-reporter.md §7.5.
 *
 * Nằm ngoài `_app` (như `/portal`): người mở link thường **chưa có** tài khoản, và vỏ
 * `_app` đá người chưa đăng nhập về `/login`. Ba nhánh:
 *
 * - chưa đăng nhập ⇒ form tên / email / mật khẩu. Tạo xong là có phiên ngay (server
 *   mở phiên trong lời gọi), rồi báo "đang chờ duyệt".
 * - đã đăng nhập ⇒ một nút "Gửi yêu cầu tham gia".
 * - link hỏng (hết hạn, thu hồi) ⇒ nói ngay, không bày ra form sẽ hỏng.
 *
 * Gửi xong **không** có quyền gì ở dự án: PM phải duyệt (APM-CLINV-b). Trang nói rõ
 * điều đó để khách không tưởng mình đã vào được mà không thấy dự án đâu.
 */
export const Route = createFileRoute('/client-invite/$token')({
  component: ClientInvitePage,
});

function ClientInvitePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const { status, user, logout } = useAuth();
  const [outcome, setOutcome] = useState<ClientInviteApplyStatus | null>(null);

  const preview = useQuery({
    queryKey: ['client-invite', 'preview', token],
    queryFn: () => issueApi.previewClientInvite(token),
    retry: false,
  });
  const apply = useMutation({
    mutationFn: () => issueApi.applyClientInvite(token),
    onSuccess: (data) => setOutcome(data.status),
  });

  useEffect(() => {
    // Link này thường mở ở một tab lẫn giữa mười tab khác.
    document.title = t('clientInvite.title');
  }, [t]);

  let body: ReactNode;
  if (preview.isError) {
    body = (
      <>
        <CardHeader>
          <CardTitle>{t('clientInvite.invalidTitle')}</CardTitle>
          <CardDescription>{t('clientInvite.invalid')}</CardDescription>
        </CardHeader>
      </>
    );
  } else if (!preview.data || status === 'restoring') {
    body = (
      <CardContent className="pt-6">
        <Skeleton className="h-32" />
      </CardContent>
    );
  } else {
    const { projectName, projectKey, orgName } = preview.data;
    body = (
      <>
        <CardHeader>
          <CardTitle>{t('clientInvite.heading', { project: projectName })}</CardTitle>
          <CardDescription>
            {t('clientInvite.subheading', { org: orgName, key: projectKey })}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {outcome ? (
            <Outcome status={outcome} />
          ) : status === 'authenticated' && user ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm">{t('clientInvite.signedInAs', { name: user.displayName })}</p>
              {apply.isError && (
                <p role="alert" className="text-sm text-rag-red">
                  {errorMessage(apply.error, t)}
                </p>
              )}
              <Button disabled={apply.isPending} onClick={() => apply.mutate()}>
                {t('clientInvite.apply')}
              </Button>
              <button
                type="button"
                className="self-start text-xs text-muted-foreground underline"
                onClick={() => void logout()}
              >
                {t('clientInvite.notYou')}
              </button>
            </div>
          ) : (
            /*
              Chỉ Google — người dùng chốt 2026-10-05 (APM-GAUTH-e), không còn form email
              + mật khẩu. Người đã có tài khoản quay lại đúng trang này rồi bấm "Gửi yêu
              cầu"; người mới đi qua `/onboarding?clientInvite=…`, nơi tài khoản ra đời
              cùng đơn xin vào.
            */
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">{t('clientInvite.googleHelp')}</p>
              <GoogleSignIn next={`/client-invite/${token}`} />
            </div>
          )}
        </CardContent>
      </>
    );
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-muted p-6">
      <Card className="w-full max-w-md">{body}</Card>
    </main>
  );
}

/** Kết cục sau khi gửi — mỗi trạng thái một câu, và một đường đi tiếp. */
function Outcome({ status }: { readonly status: ClientInviteApplyStatus }): ReactNode {
  const { t } = useTranslation();
  const toPortal = status === 'ok' || status === 'pending' || status === 'already_member';

  return (
    <div role="status" className="flex flex-col gap-3">
      <p className="text-sm">{t(`clientInvite.outcome.${status}`)}</p>
      <Button asChild variant={toPortal ? 'default' : 'outline'}>
        <Link to={toPortal ? '/portal' : '/'}>
          {toPortal ? t('clientInvite.toPortal') : t('clientInvite.toHome')}
        </Link>
      </Button>
    </div>
  );
}
